import { motion } from "framer-motion";
import { Info } from "lucide-react";
import { CAT_COLORS } from "@/components/common";

const FALLBACK = [["#3B6D11", "#639922"], ["#2E7D5B", "#4FB58A"], ["#8A6A08", "#D4A72C"], ["#1F5F8B", "#4A9ED8"]];
const gradOf = (key, i) => (CAT_COLORS[key]?.grad || FALLBACK[i % 4]);

export default function KeaktifanBars({ items, average, title = "Rekap Keaktifan", subtitle, testId = "keaktifan-bars" }) {
  const withData = items.filter((d) => d.hasData);
  return (
    <div className="glass rounded-3xl p-5 sm:p-6 flex flex-col" data-testid={testId}>
      <div className="flex items-center justify-between mb-1 gap-2">
        <h3 className="font-semibold text-emerald-900 dark:text-emerald-100">{title}</h3>
        {subtitle && <span className="text-xs text-slate-400">{subtitle}</span>}
      </div>
      <p className="text-xs text-slate-400 mb-5">Setiap kategori skala 0–100% (metrik independen).</p>
      <div className="space-y-4 flex-1">
        {items.map((d, i) => (
          <div key={d.key} data-testid={`${testId}-${d.key}`}>
            <div className="flex items-center justify-between text-sm mb-1.5 gap-2">
              <span className="text-slate-700 font-medium flex items-center gap-2 min-w-0"><span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: `linear-gradient(135deg, ${gradOf(d.key, i)[0]}, ${gradOf(d.key, i)[1]})` }} /><span className="truncate">{d.name}</span></span>
              {d.hasData ? <span className="font-bold text-emerald-900 dark:text-emerald-100 shrink-0">{d.persen}% <span className="text-slate-400 font-normal text-xs hidden sm:inline">({d.sub})</span></span>
                : <span className="text-xs text-slate-400 italic shrink-0">Belum ada data</span>}
            </div>
            <div className="h-3.5 w-full rounded-full bg-emerald-100/70 overflow-hidden shadow-inner">
              <motion.div className="h-full rounded-full" title={d.hasData ? `${d.persen}%` : "Belum ada data"}
                style={{ background: d.hasData ? `linear-gradient(90deg, ${gradOf(d.key, i)[0]}, ${gradOf(d.key, i)[1]})` : "repeating-linear-gradient(45deg,#e5e7eb 0 6px,#f3f4f6 6px 12px)", boxShadow: d.hasData ? "inset 0 -3px 0 rgba(0,0,0,0.18), 0 2px 8px rgba(23,52,4,0.25)" : "none", originX: 0 }}
                initial={{ width: 0 }} animate={{ width: d.hasData ? `${Math.max(d.persen, 1.5)}%` : "100%" }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.08 * i }} whileHover={{ scaleY: 1.25 }} />
            </div>
          </div>
        ))}
      </div>
      <div className="mt-5 pt-4 border-t border-emerald-100 flex items-center justify-between gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-wide text-slate-400">Rata-rata keseluruhan</p>
          <p className="text-3xl font-extrabold text-emerald-800 leading-none mt-1" data-testid={`${testId}-average`}>{withData.length ? `${average}%` : "—"}</p>
        </div>
        <p className="text-[11px] text-slate-400 max-w-[200px] text-right flex gap-1 items-start"><Info className="h-3 w-3 shrink-0 mt-0.5" />Dihitung dari {withData.length} kategori yang sudah memiliki data; kategori tanpa data tidak dihitung sebagai 0%.</p>
      </div>
    </div>
  );
}

export function toBarItems(summary) {
  const ng = summary.ngaji_dhuha, dep = summary.keaktifan_departemen, rib = summary.keaktifan_ribath, ku = summary.keaktifan_kuliah || {};
  return [
    { key: "ngaji", name: "Ngaji Dhuha", persen: ng.persen, hasData: ng.total > 0 || !!ng.dari_sheet, sub: ng.dari_sheet ? "dari Sheet" : `${ng.hadir}/${ng.total}` },
    { key: "departemen", name: "Keaktifan Departemen", persen: dep.persen, hasData: dep.total > 0 || !!dep.dari_sheet, sub: dep.dari_sheet ? "dari Sheet" : `${dep.hadir}/${dep.total}` },
    { key: "ribath", name: "Keaktifan Ribath", persen: rib.persen, hasData: rib.total > 0 || !!rib.dari_sheet, sub: rib.dari_sheet ? "dari Sheet" : `${rib.hadir}/${rib.total}` },
    { key: "kuliah", name: "Keaktifan Kuliah", persen: ku.persen ?? 0, hasData: !!ku.ada_data, sub: "dari kampus" },
  ];
}
